
import "./styles.scss"
import { observer } from 'mobx-react';
import { getProgressColor, getStatusColor } from './../../../../utilities/getStatusColor';
import { formatValueWithUnits } from './../../../../utilities/formatValueWithUnits';
import { useDashboardDict } from './../../../../context/useDashboardDict';
import { IUnitValue, UserDashboardWidgetModel } from 'shared/models';
import { ICONS } from 'shared/constants';
import { UiIcon } from 'shared/uikit';
import React from 'react';

type PropsType = {
    widget: UserDashboardWidgetModel
}

const getProgressIcon = (progress: number): string => {
    if (progress > 100) return 'FACE_EXCELLENT';
    if (progress === 100) return 'FACE_HAPPY';
    if (progress >= 80) return 'FACE_SMILE';
    if (progress >= 50) return 'FACE_NEUTRAL';
    if (progress >= 20) return 'FACE_SAD';
    return 'FACE_VERY_SAD';
};

const getSingleValue = (v?: IUnitValue | IUnitValue[] | null): IUnitValue | null => {
    if (!v) return null;
    return Array.isArray(v) ? v[0] : v;
};

export const PPDWidgetProgress = observer(({widget}: PropsType) => {
    const item = widget.items[0];

    const factValue = getSingleValue(item.fact);
    const planValue = getSingleValue(item.plan);
    const progressValue = getSingleValue(item.value);

    const progress = progressValue?.amount ?? 0;
    const fact = factValue?.amount ?? 0;
    const plan = planValue?.amount ?? 0;

    const remaining: IUnitValue = {
        amount: Math.max((plan || 0) - (fact || 0), 0),
        unit_id: planValue?.unit_id || factValue?.unit_id || progressValue?.unit_id || 'currency',
    };

    const isOver100 = progress > 100;
    const color = getStatusColor(progress, "#2c2f35");

    const fillColorProgress = isOver100
        ? '#15803D'
        : getProgressColor(progress, 'transparent');

    const backgroundColorProgress = isOver100
        ? '#22C55E'
        : '#EAECF1';

    const displayProgress = () => {
        if (progress <= 100) return progress;
        if (progress >= 200) return 100;
        return progress - 100;
    };

    const iconName = getProgressIcon(progress) as keyof typeof ICONS;

    const quoteColor = progress >= 80 && progress < 100 ? "#2C2F35" : "#FFFFFF";

    const { units } = useDashboardDict() || {};

    const renderValue = (value?: IUnitValue | null) => {
        if (!value || value.amount == null) {
            return <div className="ppd-widget-progress__stats-value">—</div>;
        }

        const unitLabel = value.unit_id ? units?.[value.unit_id]?.long : undefined;

        return (
            <div className="ppd-widget-progress__stats-value">
                <span className="ppd-widget-progress__stats-number">
                    {formatValueWithUnits(value)}
                </span>
                {unitLabel && (
                    <span className="ppd-widget-progress__stats-unit">
                        {unitLabel}
                    </span>
                )}
            </div>
        );
    };

    return (
        <div className="ppd-widget-progress" style={{ backgroundColor: `${fillColorProgress}` }}>
            <div className="ppd-widget-progress__content">
                <div className="ppd-widget-progress__header">
                    <h3 className="ppd-widget-progress__title">{widget.name}</h3>
                    <UiIcon size={28} color={color} icon={ICONS[iconName]} />
                </div>

                <div className="ppd-widget-progress__stats">
                    <div className="ppd-widget-progress__stats-row">
                        <div className="ppd-widget-progress__stats-item">
                            <div className="ppd-widget-progress__stats-label">{widget.labels.fact}</div>
                            {renderValue(widget.items[0].fact as IUnitValue)}
                        </div>
                        <div className="ppd-widget-progress__stats-item">
                            <div className="ppd-widget-progress__stats-label">{widget.labels.plan}</div>
                            {renderValue(widget.items[0].plan as IUnitValue)}
                        </div>
                    </div>

                    <div className="ppd-widget-progress__progress">
                        <div className="ppd-widget-progress__progress-label" style={{ color }}>{progress}%</div>
                        <div className="ppd-widget-progress__progress-bar-wrapper" style={{ backgroundColor: backgroundColorProgress }}>
                            <div className="ppd-widget-progress__progress-bar"
                                style={{
                                    width: `${displayProgress()}%`,
                                    backgroundColor: fillColorProgress
                                }}
                            ></div>
                        </div>
                    </div>
                </div>

                <div className="ppd-widget-progress__footer">
                    <span className="ppd-widget-progress__footer-label">{widget.labels.difference}</span>
                    <span className="ppd-widget-progress__footer-value">{formatValueWithUnits(remaining, units)}</span>
                </div>
            </div>
            {
                widget?.labels?.quote && (
                    <div className="ppd-widget-progress__quote">
                        <UiIcon size={16} color={quoteColor} icon={ICONS.QUOTE}/>
                        <p className="ppd-widget-progress__quote-text" style={{ color: quoteColor }}>
                            { widget?.labels?.quote }
                        </p>
                    </div>
                )
            }
        </div>
    );
});
